import type { LensCard } from '~/types/leLens';

/** Measure a summary at its arranged width, including its real header,
 * description and Markdown. Reading a Lens never changes saved geometry:
 * this runs only for the editor's explicit Smart arrange action. */
export function measureInsightHeights(
  cards: ReadonlyArray<LensCard>,
  root: ParentNode = document,
): Map<string, number> {
  const insights = new Set(cards.filter((card) => card.type === 'insight').map((card) => card.id));
  const heights = new Map<string, number>();
  root.querySelectorAll<HTMLElement>('[data-lens-card-frame]').forEach((frame) => {
    const id = frame.dataset.lensCardFrame;
    if (!id || !insights.has(id)) return;
    const content = frame.querySelector<HTMLElement>('[data-lens-insight-content]');
    const shell = content?.closest<HTMLElement>('.MuiCard-root');
    const canvas = frame.closest<HTMLElement>('.react-grid-layout');
    const width = canvas?.getBoundingClientRect().width ?? 0;
    // Loading/error cards retain a safe default until their answer is ready.
    if (!shell || !canvas || width <= 0) return;

    const copy = shell.cloneNode(true) as HTMLElement;
    copy.setAttribute('aria-hidden', 'true');
    copy.setAttribute('inert', '');
    copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    Object.assign(copy.style, {
      position: 'absolute',
      visibility: 'hidden',
      pointerEvents: 'none',
      top: '0',
      left: '0',
      width: `${width}px`,
      height: 'auto',
      minHeight: '0',
      maxHeight: 'none',
    });
    // Release only the text body's fixed frame constraints. The shell's
    // actual typography, padding and container queries still do the sizing.
    copy
      .querySelectorAll<HTMLElement>(
        '[data-lens-card-body], [data-lens-insight-content], [data-lens-insight-content] > *',
      )
      .forEach((node) => Object.assign(node.style, { height: 'auto', maxHeight: 'none', flex: 'none' }));
    try {
      canvas.appendChild(copy);
      const height = Math.ceil(copy.getBoundingClientRect().height);
      if (Number.isFinite(height) && height > 0) heights.set(id, height);
    } finally {
      copy.remove();
    }
  });
  return heights;
}
